<script setup lang="ts">
    import { ref, onBeforeUnmount } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import EchoButton from './EchoButton.vue';
    import { TextInputWidth } from './TextInput.vue';
    import { CheckIcon, CopyIcon } from '@lucide/vue';

    defineProps<{
        width: TextInputWidth;
        size?: 'small' | 'large';
    }>();

    const emit = defineEmits<{
        (e: 'copy', value: string): void;
    }>();

    const model = defineModel<string>();

    const copied = ref<boolean>(false);
    const copyTimeout = ref<number | undefined>();

    onBeforeUnmount(() => {
        clearTimeout(copyTimeout.value);
    });

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(model.value ?? '');
            copied.value = true;
            emit('copy', model.value ?? '');

            clearTimeout(copyTimeout.value);
            copyTimeout.value = window.setTimeout(() => {
                copied.value = false;
            }, 2000);
        } catch (error) {
            console.error('Failed to copy:', error);
        }
    };
</script>

<template>
    <div class="relative flex" :class="width">
        <input
            v-model="model"
            type="text"
            readonly
            class="w-full TextInput rounded-tr-none rounded-br-none pointer-events-auto"
        />
        <div
            class="bg-gray-lightest pointer-events-auto px-3 border border-l-0 border-gray-light items-center flex rounded-tr-sm rounded-br-sm"
        >
            <EchoButton v-if="!copied" variant="tertiary" @click="copyToClipboard">
                {{ __('copy') }}
                <template #icon-right><CopyIcon /></template>
            </EchoButton>
            <EchoButton v-else variant="tertiary">
                {{ __('copied') }}
                <template #icon-right><CheckIcon /></template>
            </EchoButton>
        </div>
    </div>
</template>
